import { User } from '@src/entities/User/ui/components/User';
import { useCurrentUser } from '@src/entities/User/ui/hooks/useCurrentUser';
import { Button } from '@src/shared/ui/components/Button';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { TextInputArea } from '@uhg-abyss/web/ui/TextInputArea';
import { ToggleSwitch } from '@uhg-abyss/web/ui/ToggleSwitch';
import { isEmpty, merge } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import fields from './includes/fields.json';

const { categoryCode, categoryDesc, isValid } = fields;

/**
 * FormModal
 *
 * Prompts the user with a popup window allowing them to edit the code category.
 *
 * @param currentEntity
 * @param handleSave
 * @param isOpen
 * @param setCurrentEntity
 * @param setIsOpen
 * @returns {Element}
 * @constructor
 */
export function FormModal({
  currentEntity = {},
  handleSave = async () => {},
  isOpen = false,
  setCurrentEntity = () => {},
  setIsOpen = () => {},
}) {
  let mode = 'create';

  if (!isEmpty(currentEntity)) {
    mode = 'edit';
  }

  const defaultValues = {
    categoryCode: '',
    categoryDesc: '',
    isValid: true,
  };

  const [isDisabled, setIsDisabled] = useState(true);
  const [initialValues, setInitialValues] = useState({});

  const form = useForm({ defaultValues });

  const { hasPermission } = useCurrentUser();

  const statusValue = form?.getValues('isValid');

  const canActivate = hasPermission('Admin:CodeCategories', 'activate');
  const canDeactivate = hasPermission('Admin:CodeCategories', 'deactivate');

  const canToggle = useMemo(() => {
    if (statusValue === true) {
      return canDeactivate;
    }

    if (statusValue === false) {
      return canActivate;
    }

    return false;
  }, [statusValue, canDeactivate, canActivate]);

  /**
   * Mapping data loaded from API to the form state.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      const data = merge({}, defaultValues, {
        categoryCode: currentEntity?.categoryCode,
        categoryDesc: currentEntity?.categoryDesc,
        isValid: currentEntity?.isValid,
      });

      if (isEmpty(initialValues)) {
        setInitialValues(data);
      }

      form?.reset(data, {
        keepDirty: false,
        keepDirtyValues: false,
        keepErrors: false,
        keepIsValid: false,
        keepSubmitCount: true,
        keepTouched: false,
        keepValues: false,
      });
    }
  }, [currentEntity]);

  /**
   * handleClose
   *
   * Handles the closing of the modal.
   *
   * @returns {Promise<void>}
   */
  const handleClose = async () => {
    setIsOpen(false);
    setCurrentEntity({});
  };

  /**
   * handleSubmit
   *
   * Handles the form submission.
   *
   * @param submittedValues
   * @returns {Promise<void>}
   */
  const handleSubmit = async (submittedValues) => {
    await handleClose();

    const payload = {};

    payload.categoryCode = String(submittedValues?.categoryCode);
    payload.categoryDesc = String(submittedValues?.categoryDesc);
    payload.isValid = Boolean(submittedValues?.isValid);

    await handleSave({ data: payload, mode });
  };

  const formValues = form?.getValues();

  /**
   * isEqual
   *
   * Compares two objects to determine if they are equal.
   *
   * @param object1
   * @param object2
   * @returns {boolean}
   */
  const isEqual = (object1, object2) => {
    return JSON.stringify(object1) === JSON.stringify(object2);
  };

  /**
   * form validation rules, toggling the save button enablement.
   */
  useEffect(() => {
    if (!isEmpty(currentEntity)) {
      if (isEqual(formValues, initialValues) || !form?.formState?.isValid) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    }

    if (isEmpty(currentEntity)) {
      if (!form?.formState?.isValid) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    }
  }, [currentEntity, form?.formState.isDirty, form?.formState?.isValid, formValues, form?.formState?.defaultValues]);

  return (
    <ErrorHandler location="src/features/Codes/ManageCodeCategory/components/FormModal/FormModal.jsx">
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={mode === 'edit' ? 'Edit Code Category' : 'Create Code Category'}
      >
        <FormProvider autoComplete="off" onSubmit={handleSubmit} state={form}>
          <Modal.Section>
            <Grid>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <TextInput
                  {...categoryCode}
                  isDisabled={mode === 'edit'}
                  onChange={() => {
                    form?.validate(
                      `categoryCode`,
                      () => {},
                      () => {}
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col
                span={{
                  xs: '100%',
                }}
              >
                <TextInputArea
                  {...categoryDesc}
                  onChange={() => {
                    form?.validate(
                      `categoryDesc`,
                      () => {},
                      () => {}
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: '100%' }}>
                <Layout.Group>
                  <div>Valid</div>
                  <User.Capability
                    alias="toggle"
                    attributes={['activate', 'deactivate']}
                    isAllowed={canToggle}
                    resource="Admin:CodeCategories"
                  >
                    <ToggleSwitch {...isValid} />
                  </User.Capability>
                </Layout.Group>
              </Grid.Col>
            </Grid>
          </Modal.Section>
          <Modal.Section>
            <Divider height={1} />
            <Layout.Group alignLayout="right">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button isDisabled={isDisabled} type="submit" variant="solid">
                {mode === 'edit' ? 'Save' : 'Create'}
              </Button>
            </Layout.Group>
          </Modal.Section>
        </FormProvider>
      </Modal>
    </ErrorHandler>
  );
}
